import { motion, AnimatePresence } from 'framer-motion';

/**
 * Reusable modal shell — used by confirm dialogs, create popups,
 * settings modals, and the data-source wizard.
 *
 * Uses the ``modal-overlay`` / ``modal`` / ``modal-header`` /
 * ``modal-body`` / ``modal-footer`` CSS classes defined in
 * ``styles/settings.css``.
 *
 * Props:
 *  - show        : boolean
 *  - title       : string rendered in the header
 *  - onClose     : () => void  (called on overlay click or close button)
 *  - children    : body content (ReactNode)
 *  - footer      : footer content (ReactNode, optional)
 *  - modalStyle  : extra inline style for the modal div (optional)
 *  - modalClass  : extra CSS class for the modal div (optional)
 *  - headerRight : content to render in the header alongside the close button (optional)
 *  - headerLeft  : content to render on the left side of the header (optional)
 *  - headerCenter: content to render in the middle of the header in place of the title (optional)
 *  - showClose   : whether to render the close "×" button (default true)
 */
export default function Modal({
  show,
  title,
  onClose,
  children,
  footer,
  modalStyle,
  modalClass = '',
  headerRight,
  headerLeft,
  headerCenter,
  showClose = true,
}) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className={`modal${modalClass ? ' ' + modalClass : ''}`}
            style={modalStyle}
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              {headerLeft && <div className="modal-header-left">{headerLeft}</div>}
              {headerCenter ? (
                <div className="modal-header-center">{headerCenter}</div>
              ) : (
                <h3 className="modal-title">{title}</h3>
              )}
              {headerRight && <div className="modal-header-right">{headerRight}</div>}
              {showClose && <button className="modal-close" onClick={onClose}>&times;</button>}
            </div>

            <div className="modal-body">{children}</div>

            {footer && <div className="modal-footer">{footer}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
